/**
 * Bootstrap engine — a tiny, framework-agnostic state machine that runs an
 * ordered list of async startup steps behind a global loader.
 *
 * It deliberately avoids any state library: module-level state + a listener set,
 * meant to be consumed from React via `useSyncExternalStore` (see
 * `useBootstrapState`). Configure the steps for a given context with
 * `setBootstrapSteps`, then `runBootstrap()`. To re-run for a new context (e.g. a
 * workspace/role switch), call `resetBootstrap()` then `runBootstrap()`.
 *
 * Extending: adding a new startup loading phase is just appending another
 * `BootstrapStep` to the array passed to `setBootstrapSteps` — its `message`
 * shows under the loader while its `run()` is in flight.
 */

export type BootstrapStatus = 'pending' | 'running' | 'ready' | 'error';

export interface BootstrapStep {
  /** Stable identifier, surfaced as the active step id while running. */
  id: string;
  /** Message shown beneath the global loader while this step runs. */
  message: string;
  /** Perform the work. Resolve `false` to stop the sequence early (skip the rest). */
  run: () => Promise<boolean>;
}

export interface BootstrapState {
  status: BootstrapStatus;
  /** The running step's message, or null when pending/ready/error. */
  message: string | null;
  error: Error | null;
  activeStepId: string | null;
}

const INITIAL_STATE: BootstrapState = {
  status: 'pending',
  message: null,
  error: null,
  activeStepId: null,
};

let state: BootstrapState = INITIAL_STATE;
let steps: BootstrapStep[] = [];
const listeners = new Set<() => void>();

// Monotonic run token. Bumped on every reset and run so that a stale in-flight
// run (e.g. after a workspace switch triggered a re-run) can never clobber the
// state of the current one.
let epoch = 0;

function emit(): void {
  listeners.forEach((listener) => listener());
}

function setState(next: BootstrapState, forEpoch: number): void {
  if (forEpoch !== epoch) return;
  state = next;
  emit();
}

export function getBootstrapState(): BootstrapState {
  return state;
}

export function subscribeBootstrapState(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function setBootstrapSteps(next: BootstrapStep[]): void {
  steps = next;
}

/** Cancel any in-flight run and return to the pending state. */
export function resetBootstrap(): void {
  epoch += 1;
  state = INITIAL_STATE;
  emit();
}

/**
 * Run the configured steps in order. Safe to call repeatedly; each call is
 * tagged with a fresh epoch, so only the latest run's transitions take effect.
 */
export function runBootstrap(): Promise<void> {
  const runEpoch = (epoch += 1);

  return (async () => {
    try {
      for (const step of steps) {
        if (runEpoch !== epoch) return;
        setState(
          { status: 'running', message: step.message, error: null, activeStepId: step.id },
          runEpoch
        );
        // Steps run strictly in order (later steps may depend on earlier ones),
        // so sequential awaiting is intentional here.
        // (Directive is consumed by oxlint; eslint no longer reports here.)
        // eslint-disable-next-line no-await-in-loop
        const proceed = await step.run();
        if (runEpoch !== epoch) return;
        if (!proceed) break;
      }
      setState({ status: 'ready', message: null, error: null, activeStepId: null }, runEpoch);
    } catch (error) {
      setState(
        { status: 'error', message: null, error: error as Error, activeStepId: null },
        runEpoch
      );
    }
  })();
}
